import React, { useCallback, useEffect, useMemo, useState } from "react";

import { ArrayUtils, useTranslation } from "utils";

import { CROP_TYPE_FCPO, CropFCPO, CropsWithMissingHistoricalPracticeDataByYear } from "models";

import { Button, CheckboxSegmented, CropFilterEmptyState, Flex, Icon, Modal, Tooltip, Typography } from "components";

export type HistoricalPracticeFiltersState = {
  appliedCropIds: string[];
  previouslyAppliedCropIds: string[];
  missingGrowingPeriod: boolean;
  missingOrganicFertilizers: boolean;
  missingSeedings: boolean;
  missingTillages: boolean;
};

type CropOption = {
  cropId: string;
  cropType: CROP_TYPE_FCPO;
  translationSlug: string;
};

type CropFilterSectionProps = {
  allOptionLabel: string;
  hasAllOption: boolean;
  allSelected: boolean;
  options: CropOption[];
  internalCropIds: string[];
  onToggleAllForType: () => void;
  onToggleCropId: (cropId: string) => void;
  emptyMessageKey: string;
  t: (key: string) => string;
};

const CropFilterSection = ({
  allOptionLabel,
  hasAllOption,
  allSelected,
  options,
  internalCropIds,
  onToggleAllForType,
  onToggleCropId,
  emptyMessageKey,
  t,
}: CropFilterSectionProps) => (
  <Flex vertical gap="1rem" className="historical-practice-data-table-view-filters-crops">
    <Flex vertical gap="0.5rem">
      {options.length ? (
        <Flex className="historical-practice-data-table-view-filters-crops-checkboxes" wrap="wrap" gap="0.5rem">
          {hasAllOption && <CheckboxSegmented label={allOptionLabel} checked={allSelected} onChange={onToggleAllForType} />}
          {options
            .sort((a, b) => a.translationSlug.localeCompare(b.translationSlug))
            .map((option) => (
              <CheckboxSegmented
                key={option.cropId}
                label={t(`practices:crops.${option.translationSlug}`)}
                checked={internalCropIds.includes(option.cropId)}
                onChange={() => onToggleCropId(option.cropId)}
              />
            ))}
        </Flex>
      ) : (
        <CropFilterEmptyState message={t(emptyMessageKey)} />
      )}
    </Flex>
  </Flex>
);

const filterByExistence = (crop?: CropFCPO): crop is CropFCPO => !!crop;

export const PracticeFilters = ({
  cropsWithMissingHistoricalPracticeDataByYear,
  selectedYear,
  filters,
  setFilters,
  onClearAll,
}: {
  cropsWithMissingHistoricalPracticeDataByYear: CropsWithMissingHistoricalPracticeDataByYear;
  selectedYear: number | undefined;
  filters: HistoricalPracticeFiltersState;
  setFilters: React.Dispatch<React.SetStateAction<HistoricalPracticeFiltersState>>;
  onClearAll: () => void;
}) => {
  const { t } = useTranslation(["historicalPracticeDataTableView", "practices"]);

  const [showCropsModal, setShowCropsModal] = useState(false);
  const [internalCropIds, setInternalCropIds] = useState<string[]>([]);
  const [internalPreviousCropIds, setInternalPreviousCropIds] = useState<string[]>([]);
  const [cropFilterTab, setCropFilterTab] = useState<CROP_TYPE_FCPO | "PREVIOUS">(CROP_TYPE_FCPO.MAIN);

  const allYearEntries = useMemo(() => Object.values(cropsWithMissingHistoricalPracticeDataByYear).flat(), [cropsWithMissingHistoricalPracticeDataByYear]);
  const selectedYearEntries = useMemo(
    () => (selectedYear ? cropsWithMissingHistoricalPracticeDataByYear[selectedYear] ?? [] : []),
    [selectedYear, cropsWithMissingHistoricalPracticeDataByYear]
  );

  const allCropOptionsById = useMemo(() => {
    const map = new Map<string, CropOption>();
    for (const entries of Object.values(cropsWithMissingHistoricalPracticeDataByYear)) {
      for (const entry of entries) {
        const { crop } = entry;
        if (!map.has(crop.cropId)) {
          map.set(crop.cropId, { cropId: crop.cropId, cropType: crop.cropType, translationSlug: crop.translationSlug });
        }
      }
    }
    return map;
  }, [cropsWithMissingHistoricalPracticeDataByYear]);

  const selectedYearCropOptions = useMemo<CropOption[]>(() => {
    const map = new Map<string, CropOption>();
    for (const entry of selectedYearEntries) {
      const { crop } = entry;
      if (!map.has(crop.cropId)) {
        map.set(crop.cropId, { cropId: crop.cropId, cropType: crop.cropType, translationSlug: crop.translationSlug });
      }
    }
    // add already applied crop ids to the map
    for (const cropId of filters.appliedCropIds) {
      if (!map.has(cropId)) {
        const option = allCropOptionsById.get(cropId);
        if (option) map.set(cropId, option);
      }
    }
    return Array.from(map.values());
  }, [selectedYearEntries, filters.appliedCropIds, allCropOptionsById]);

  const previousCropOptions = selectedYear
    ? ArrayUtils.uniqueBy(allYearEntries.map((entry) => entry.previousMainCrop).filter(filterByExistence), (crop) => crop.cropId)
    : [];
  const mainCropOptions = useMemo(() => selectedYearCropOptions.filter((o) => o.cropType === CROP_TYPE_FCPO.MAIN), [selectedYearCropOptions]);
  const coverCropOptions = useMemo(() => selectedYearCropOptions.filter((o) => o.cropType === CROP_TYPE_FCPO.COVER), [selectedYearCropOptions]);

  const requiredPractices = useMemo(() => {
    if (!selectedYear) {
      return {
        seedings: false,
        tillages: false,
        organicFertilizers: false,
      };
    }

    const entries = cropsWithMissingHistoricalPracticeDataByYear[selectedYear] ?? [];
    return {
      seedings: entries.some((e) => e.assignedPractices.seedings != null),
      tillages: entries.some((e) => e.assignedPractices.tillages != null),
      organicFertilizers: entries.some((e) => e.assignedPractices.organicFertilizers != null),
    };
  }, [selectedYear, cropsWithMissingHistoricalPracticeDataByYear]);

  // prevent hidden toggles from remaining active when selected year or required practices change
  const { seedings: reqSeedings, tillages: reqTillages, organicFertilizers: reqOrganic } = requiredPractices;
  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      missingSeedings: reqSeedings ? prev.missingSeedings : false,
      missingTillages: reqTillages ? prev.missingTillages : false,
      missingOrganicFertilizers: reqOrganic ? prev.missingOrganicFertilizers : false,
    }));
  }, [selectedYear, reqSeedings, reqTillages, reqOrganic, setFilters]);

  const hasAnyFiltersApplied =
    filters.appliedCropIds.length > 0 ||
    filters.previouslyAppliedCropIds.length > 0 ||
    filters.missingOrganicFertilizers ||
    filters.missingSeedings ||
    filters.missingTillages;

  const handleCropsModalOpen = () => {
    if (!selectedYear) return;
    setInternalCropIds(filters.appliedCropIds);
    setInternalPreviousCropIds(filters.previouslyAppliedCropIds);
    setCropFilterTab(CROP_TYPE_FCPO.MAIN);
    setShowCropsModal(true);
  };

  const toggleInternalCropId = (cropId: string) => {
    setInternalCropIds((prev) => (prev.includes(cropId) ? prev.filter((id) => id !== cropId) : [...prev, cropId]));
  };

  const toggleInternalPreviousCropId = (cropId: string) => {
    setInternalPreviousCropIds((prev) => (prev.includes(cropId) ? prev.filter((id) => id !== cropId) : [...prev, cropId]));
  };

  const getAllCropIdsForType = useCallback(
    (cropType: CROP_TYPE_FCPO) => (cropType === CROP_TYPE_FCPO.MAIN ? mainCropOptions : coverCropOptions).map((o) => o.cropId),
    [mainCropOptions, coverCropOptions]
  );

  const toggleAllForType = (cropType: CROP_TYPE_FCPO) => {
    const allIds = getAllCropIdsForType(cropType);
    if (allIds.length === 0) return;

    const hasAll = allIds.every((id) => internalCropIds.includes(id));
    setInternalCropIds((prev) => {
      if (hasAll) return prev.filter((id) => !allIds.includes(id));
      const next = new Set(prev);
      allIds.forEach((id) => next.add(id));
      return Array.from(next);
    });
  };

  const toggleAllForPreviousCrops = () => {
    const allIds = previousCropOptions.map((option) => option.cropId);
    if (allIds.length === 0) return;

    const hasAll = allIds.every((id) => internalPreviousCropIds.includes(id));
    setInternalPreviousCropIds((prev) => {
      if (hasAll) return prev.filter((id) => !allIds.includes(id));
      const next = new Set(prev);
      allIds.forEach((id) => next.add(id));
      return Array.from(next);
    });
  };

  const handleApplyCropFilters = () => {
    setFilters((prev) => ({
      ...prev,
      appliedCropIds: internalCropIds,
      previouslyAppliedCropIds: internalPreviousCropIds,
    }));
    setShowCropsModal(false);
  };

  const handleClearCropInternal = () => {
    setInternalCropIds([]);
    setInternalPreviousCropIds([]);
  };

  const allMainSelected = mainCropOptions.length > 0 && mainCropOptions.every(({ cropId }) => internalCropIds.includes(cropId));
  const allCoverSelected = coverCropOptions.length > 0 && coverCropOptions.every(({ cropId }) => internalCropIds.includes(cropId));
  const allPreviousSelected = previousCropOptions.length > 0 && previousCropOptions.every(({ cropId }) => internalPreviousCropIds.includes(cropId));

  const mainHasAllOption = mainCropOptions.length > 1;
  const coverHasAllOption = coverCropOptions.length > 1;
  const previousHasAllOption = previousCropOptions.length > 1;

  const cropFilterCount = filters.appliedCropIds.length + filters.previouslyAppliedCropIds.length;

  const selectedCropOptionsById = useMemo(() => {
    const map = new Map<string, CropOption>();
    for (const option of selectedYearCropOptions) map.set(option.cropId, option);
    return map;
  }, [selectedYearCropOptions]);

  const filterOptionsCountTooltipContent = filters.appliedCropIds
    .map((cropId) => selectedCropOptionsById.get(cropId)?.translationSlug)
    .filter(Boolean)
    .map((slug) => t(`practices:crops.${slug}`))
    .join(", ");

  return (
    <Flex className="historical-practice-data-table-view-filters-bar" align="center" justify="space-between" gap="0.5rem">
      <Flex className="historical-practice-data-table-view-filters-left" align="center" gap="0.5rem" wrap="wrap">
        <Typography variant="paragraph-m" color="grey-600" noWrap className="margin-0">
          {t("historicalPracticeDataTableView:table.header.filters.filterBy", { defaultValue: "Filter by" })}
        </Typography>
        <div className="historical-practice-data-table-view-filters-crops">
          <Flex className={`historical-practice-data-table-view-filters-crops-button ${showCropsModal ? "active" : ""} ${!selectedYear ? "disabled" : ""}`}>
            <Flex
              className={`historical-practice-data-table-view-filters-crops-trigger ${cropFilterCount > 0 ? "with-delete-option" : ""}`}
              align="center"
              gap="0.5rem"
              onClick={handleCropsModalOpen}
              role="button"
              tabIndex={0}
              aria-label="Crops filters"
            >
              <Typography variant="paragraph-s" className="margin-0">
                {t("historicalPracticeDataTableView:table.header.filters.crops")}
              </Typography>
              {cropFilterCount > 0 && (
                <Tooltip
                  placement="top"
                  title={filterOptionsCountTooltipContent}
                  arrow={false}
                  classNames={{ root: "historical-practice-data-table-view-tooltip" }}
                  className="historical-practice-data-table-view-tooltip-span"
                >
                  <Flex className="historical-practice-data-table-view-filters-crops-count" align="center" justify="center">
                    <Typography variant="paragraph-s" bold className="margin-0">
                      {cropFilterCount}
                    </Typography>
                  </Flex>
                </Tooltip>
              )}
            </Flex>
            <Flex
              className={`historical-practice-data-table-view-filters-crops-clear ${cropFilterCount > 0 ? "visible" : ""}`}
              align="center"
              justify="center"
              onClick={() => setFilters((prev) => ({ ...prev, appliedCropIds: [], previouslyAppliedCropIds: [] }))}
              role="button"
              tabIndex={0}
              aria-label="Clear crop filters"
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setFilters((prev) => ({ ...prev, appliedCropIds: [], previouslyAppliedCropIds: [] }));
                }
              }}
            >
              <Icon iconName="tabler/x" size="s" />
            </Flex>
          </Flex>

          <Modal
            cancelText={t("historicalPracticeDataTableView:table.header.filters.clearAll")}
            className="historical-practice-data-table-view-filters-crops-modal"
            destroyOnHidden
            okText={t("historicalPracticeDataTableView:table.header.filters.applyFilters")}
            onCancel={(e) => {
              const target = e.target as HTMLElement;
              const button = target.closest("button");
              // if the clicked target is the modal close button, close the modal and don't apply changes
              if (button?.classList.contains("ant-modal-close")) {
                setShowCropsModal(false);
              } else {
                handleClearCropInternal();
              }
            }}
            onOk={handleApplyCropFilters}
            showModal={showCropsModal}
            title={t("historicalPracticeDataTableView:table.header.filters.filterByCrops")}
            content={
              <Flex vertical gap="2rem" className="historical-practice-data-table-view-filters-crops-modal-content">
                <Flex className="historical-practice-data-table-view-filters-crops-tabs" gap="0">
                  <div
                    role="button"
                    className={`historical-practice-data-table-view-filters-crops-tab ${cropFilterTab === CROP_TYPE_FCPO.MAIN ? "active" : ""}`}
                    onClick={() => setCropFilterTab(CROP_TYPE_FCPO.MAIN)}
                  >
                    {t("historicalPracticeDataTableView:table.header.filters.mainCrops")}
                  </div>
                  <div
                    role="button"
                    className={`historical-practice-data-table-view-filters-crops-tab ${cropFilterTab === CROP_TYPE_FCPO.COVER ? "active" : ""}`}
                    onClick={() => setCropFilterTab(CROP_TYPE_FCPO.COVER)}
                  >
                    {t("historicalPracticeDataTableView:table.header.filters.coverCrops")}
                  </div>
                  <div
                    role="button"
                    className={`historical-practice-data-table-view-filters-crops-tab ${cropFilterTab === "PREVIOUS" ? "active" : ""}`}
                    onClick={() => setCropFilterTab("PREVIOUS")}
                  >
                    {t("historicalPracticeDataTableView:table.header.filters.previousCrops")}
                  </div>
                </Flex>
                {cropFilterTab === CROP_TYPE_FCPO.MAIN && (
                  <CropFilterSection
                    allOptionLabel={t("historicalPracticeDataTableView:table.header.filters.allMainCrops")}
                    hasAllOption={mainHasAllOption}
                    allSelected={allMainSelected}
                    options={mainCropOptions}
                    internalCropIds={internalCropIds}
                    onToggleAllForType={() => toggleAllForType(CROP_TYPE_FCPO.MAIN)}
                    onToggleCropId={toggleInternalCropId}
                    emptyMessageKey="historicalPracticeDataTableView:table.header.filters.noMainCrops"
                    t={t}
                  />
                )}
                {cropFilterTab === CROP_TYPE_FCPO.COVER && (
                  <CropFilterSection
                    allOptionLabel={t("historicalPracticeDataTableView:table.header.filters.allCoverCrops")}
                    hasAllOption={coverHasAllOption}
                    allSelected={allCoverSelected}
                    options={coverCropOptions}
                    internalCropIds={internalCropIds}
                    onToggleAllForType={() => toggleAllForType(CROP_TYPE_FCPO.COVER)}
                    onToggleCropId={toggleInternalCropId}
                    emptyMessageKey="historicalPracticeDataTableView:table.header.filters.noCoverCrops"
                    t={t}
                  />
                )}
                {cropFilterTab === "PREVIOUS" && (
                  <CropFilterSection
                    allOptionLabel={t("historicalPracticeDataTableView:table.header.filters.allPreviousCrops")}
                    hasAllOption={previousHasAllOption}
                    allSelected={allPreviousSelected}
                    options={previousCropOptions}
                    internalCropIds={internalPreviousCropIds}
                    onToggleAllForType={toggleAllForPreviousCrops}
                    onToggleCropId={toggleInternalPreviousCropId}
                    emptyMessageKey="historicalPracticeDataTableView:table.header.filters.noPreviousCrops"
                    t={t}
                  />
                )}
              </Flex>
            }
          />
        </div>
        <Typography variant="paragraph-m" color="grey-600" noWrap className="margin-0">
          {t("historicalPracticeDataTableView:table.header.filters.orByMissing", { defaultValue: "or by missing" })}
        </Typography>
        <CheckboxSegmented
          size="small"
          label={t("historicalPracticeDataTableView:table.header.filters.growingPeriod", { defaultValue: "Growing Period" })}
          checked={filters.missingGrowingPeriod}
          onChange={() => setFilters((prev) => ({ ...prev, missingGrowingPeriod: !prev.missingGrowingPeriod }))}
        />
        {requiredPractices.seedings && (
          <CheckboxSegmented
            size="small"
            label={t("historicalPracticeDataTableView:table.header.filters.seedings", { defaultValue: "Seeding" })}
            checked={filters.missingSeedings}
            onChange={() => setFilters((prev) => ({ ...prev, missingSeedings: !prev.missingSeedings }))}
          />
        )}
        {requiredPractices.tillages && (
          <CheckboxSegmented
            size="small"
            label={t("historicalPracticeDataTableView:table.header.filters.tillages", { defaultValue: "Tillage" })}
            checked={filters.missingTillages}
            onChange={() => setFilters((prev) => ({ ...prev, missingTillages: !prev.missingTillages }))}
          />
        )}
        {requiredPractices.organicFertilizers && (
          <CheckboxSegmented
            size="small"
            label={t("historicalPracticeDataTableView:table.header.filters.organicFertilizers", { defaultValue: "Org. Fertilizer" })}
            checked={filters.missingOrganicFertilizers}
            onChange={() => setFilters((prev) => ({ ...prev, missingOrganicFertilizers: !prev.missingOrganicFertilizers }))}
          />
        )}
      </Flex>
      {hasAnyFiltersApplied && (
        <Button type="text" className="historical-practice-data-table-view-filters-clear-all" onClick={onClearAll} size="small">
          {t("historicalPracticeDataTableView:table.header.filters.clearAll")}
        </Button>
      )}
    </Flex>
  );
};
